<template>
  <form @submit="onSubmit">
    <slot />
  </form>
</template>

<script setup lang="ts">
import type { FetchError } from 'ofetch'
import { useForm } from 'vee-validate'

const props = defineProps({
  method: {
    type: Function,
    required: true,
  },
  onValidationError: {
    type: Function,
    default: null,
  },
  onError: {
    type: Function,
    default: null,
  },
  resetOnSubmit: {
    type: Boolean,
    default: true,
  },
})

const {
  handleSubmit,
  setFieldError,
  meta,
  errors,
  submitForm,
  validate,
  resetForm,
  setErrors,
} = useForm()

const submitHandler = handleSubmit(async (values, actions) => {
  if (props.method) {
    try {
      await props.method(values, actions)

      if (props.resetOnSubmit) {
        actions.resetForm()
      }
    } catch (error: any) {
      const fetchError = error as FetchError

      if (
        !fetchError.data?.errors ||
        typeof fetchError.data?.errors !== 'object' ||
        Object.keys(fetchError.data.errors).some(
          (field) => !hasFieldPath(values, field)
        )
      ) {
        if (props.onError) {
          props.onError(error)
          return
        } else {
          throw error
        }
      }

      if (props.onValidationError) {
        await props.onValidationError(fetchError.data.errors)
      }

      Object.keys(fetchError.data.errors).forEach((field) => {
        const fieldError = Array.isArray(fetchError.data.errors[field])
          ? fetchError.data.errors[field][0]
          : fetchError.data.errors[field]

        setFieldError(field, fieldError || 'Input is not correct')
      })
    }
  }
})

async function onSubmit(event: Event) {
  event.preventDefault()

  const validationResult = await validate()

  if (!validationResult.valid) {
    if (props.onValidationError) {
      await props.onValidationError(validationResult.errors)
    }

    return
  }

  await submitHandler(event)
}

defineExpose({
  meta,
  errors,
  submitForm,
  validate,
  resetForm,
  setErrors,
})

function hasFieldPath(values: Record<string, any>, field: string): boolean {
  if (field in values) {
    return true
  }

  const normalizedField = field.replace(/\[(\d+)\]/g, '.$1')
  const segments = normalizedField.split('.').filter(Boolean)

  let currentValue: any = values

  for (const segment of segments) {
    if (
      currentValue == null ||
      (typeof currentValue !== 'object' && !Array.isArray(currentValue)) ||
      !(segment in currentValue)
    ) {
      return false
    }

    currentValue = currentValue[segment]
  }

  return true
}
</script>
